// apps/client/src/components/command-palette/pageIndex.ts
// Static page index for command palette search

import {
  LayoutDashboard,
  Table2,
  Upload,
  Users,
  UserCircle,
  Building2,
  GitCompare,
  BookOpen,
  FileBarChart,
  Settings,
  Shield,
  CreditCard,
  TrendingDown,
  TrendingUp,
  Clock,
  AlertTriangle,
  DollarSign,
  Users2,
  FileText,
  Heart,
  Wallet,
  RefreshCw,
  Briefcase,
  Sparkles,
  ClipboardCheck,
  HelpCircle,
} from 'lucide-react';
import type { ElementType } from 'react';
import {
  ADMIN_REPORT_ROUTE_ROLES,
  isReportSuppressedForTenant,
  type TenantSuppressibleReportFlag,
} from '../../config/reportRegistry';

export type UserRole = 'broker' | 'broker_assistant' | 'credit_analyst' | 'marketing' | 'office_admin' | 'administrator' | 'super_admin' | 'founder';

// Admin-only report routes (RequireRole in AppRoutes): a palette hit for any other
// role would navigate to a route that redirects to "/".
const ADMIN_REPORT_PAGE_ROLES = [...ADMIN_REPORT_ROUTE_ROLES] as UserRole[];
export type SubscriptionTier = 'plus' | 'pro' | 'enterprise';

export interface SearchablePage {
  id: string;
  name: string;
  path: string;
  keywords: string[];
  icon: ElementType;
  section: 'core' | 'reports' | 'offices' | 'settings';
  requiredRoles?: UserRole[];
  requiredTier?: SubscriptionTier;
  isDynamic?: boolean; // For routes with params like /offices/:officeId
  disabled?: boolean; // Feature parked — hidden from search results
  /**
   * Report that a tenant can switch off (fail-open). When the tenant sets
   * `featureFlags[flag] = false`, RequireTenantReport bounces the route, so a
   * palette hit would navigate the user somewhere unrelated. Set this on any
   * indexed page guarded by RequireTenantReport.
   */
  suppressibleFlag?: TenantSuppressibleReportFlag;
}

export const SEARCHABLE_PAGES: SearchablePage[] = [
  // ===== CORE PAGES =====
  {
    id: 'dashboard',
    name: 'Dashboard',
    path: '/',
    keywords: ['home', 'overview', 'main', 'summary', 'kpi'],
    icon: LayoutDashboard,
    section: 'core',
  },
  {
    id: 'commissions',
    name: 'Commission Breakdown',
    path: '/commission-breakdown',
    keywords: ['commissions', 'table', 'monthly', 'data', 'breakdown'],
    icon: Table2,
    section: 'core',
  },
  {
    id: 'upload',
    name: 'Upload',
    path: '/upload',
    keywords: ['upload', 'import', 'csv', 'file', 'data'],
    icon: Upload,
    section: 'core',
  },
  {
    id: 'team',
    name: 'Team Management',
    path: '/team',
    keywords: ['team', 'members', 'users', 'people', 'staff'],
    icon: Users,
    section: 'core',
  },
  {
    id: 'client-groups',
    name: 'Client Groups',
    path: '/client-groups',
    keywords: ['client', 'groups', 'sets', 'customers', 'segments'],
    icon: Users2,
    section: 'core',
  },
  {
    id: 'contact-activity',
    name: 'Contact Activity',
    path: '/contact-activity',
    keywords: ['contact', 'activity', 'calls', 'phone', 'email', 'outreach', 'daily', 'log'],
    icon: ClipboardCheck,
    section: 'core',
    requiredRoles: ['administrator', 'super_admin'],
  },
  {
    id: 'ai-assistant',
    name: 'AI Assistant',
    path: '/ai-assistant',
    keywords: ['ai', 'assistant', 'chat', 'help', 'artificial', 'intelligence'],
    icon: Sparkles,
    section: 'core',
    requiredTier: 'enterprise',
  },

  // ===== OFFICES =====
  {
    id: 'offices',
    name: 'Manage Offices',
    path: '/offices',
    keywords: ['offices', 'branches', 'locations', 'manage'],
    icon: Building2,
    section: 'offices',
  },
  {
    id: 'offices-compare',
    name: 'Compare Offices',
    path: '/offices/compare',
    keywords: ['compare', 'offices', 'comparison', 'benchmark'],
    icon: GitCompare,
    section: 'offices',
  },
  {
    id: 'office-dashboard',
    name: 'Office Dashboard',
    path: '/offices/:officeId',
    keywords: ['office', 'dashboard', 'branch', 'location', 'view'],
    icon: Building2,
    section: 'offices',
    requiredTier: 'pro',
    isDynamic: true,
  },
  {
    id: 'brokers',
    name: 'Manage Brokers',
    path: '/brokers',
    keywords: ['brokers', 'agents', 'staff', 'employees'],
    icon: UserCircle,
    section: 'offices',
  },
  {
    id: 'loan-books',
    name: 'Loan Books',
    path: '/loan-books',
    keywords: ['loan', 'books', 'portfolio', 'acquired'],
    icon: BookOpen,
    section: 'offices',
    requiredRoles: ['super_admin'],
  },

  // ===== REPORTS - Trail Book =====
  {
    id: 'trail-run-off',
    name: 'Broker Trail Run-Off',
    path: '/reports/trail-run-off',
    keywords: ['trail', 'run', 'off', 'report', 'attrition'],
    icon: TrendingDown,
    section: 'reports',
    requiredRoles: ADMIN_REPORT_PAGE_ROLES,
  },
  {
    id: 'trail-drop-off',
    name: 'Loan Trail Drop-Off',
    path: '/reports/trail-drop-off',
    keywords: ['trail', 'drop', 'off', 'lost', 'churn'],
    icon: TrendingDown,
    section: 'reports',
    requiredTier: 'pro',
  },
  {
    id: 'trail-book-valuation',
    name: 'Trail Book Valuation',
    path: '/reports/trail-book-valuation',
    keywords: ['trail', 'book', 'valuation', 'value', 'worth'],
    icon: DollarSign,
    section: 'reports',
    requiredTier: 'pro',
    requiredRoles: ADMIN_REPORT_PAGE_ROLES,
  },
  {
    id: 'trail-forecast',
    name: 'Trail Forecast',
    path: '/reports/trail-forecast',
    keywords: ['trail', 'forecast', 'prediction', 'future', 'projection'],
    icon: TrendingUp,
    section: 'reports',
    requiredRoles: ADMIN_REPORT_PAGE_ROLES,
    requiredTier: 'pro',
  },
  {
    id: 'book-health',
    name: 'Book Health',
    path: '/reports/book-health',
    keywords: ['book', 'health', 'quality', 'status', 'condition'],
    icon: Heart,
    section: 'reports',
    requiredTier: 'pro',
  },
  {
    id: 'discharged-loans',
    name: 'Discharged Loans',
    path: '/reports/discharged-loans',
    keywords: ['discharged', 'loans', 'closed', 'paid', 'off'],
    icon: FileText,
    section: 'reports',
  },

  // ===== REPORTS - Income & Payments =====
  {
    id: 'settlement-age',
    name: 'Settlement Age',
    path: '/reports/settlement-age',
    keywords: ['settlement', 'age', 'time', 'days', 'aging'],
    icon: Clock,
    section: 'reports',
  },
  {
    id: 'clawbacks',
    name: 'Clawbacks',
    path: '/reports/clawbacks',
    keywords: ['clawbacks', 'reclaim', 'recovery', 'refund'],
    icon: AlertTriangle,
    section: 'reports',
  },
  {
    id: 'arrears',
    name: 'Arrears',
    path: '/reports/arrears',
    keywords: ['arrears', 'overdue', 'late', 'payment', 'default'],
    icon: AlertTriangle,
    section: 'reports',
    suppressibleFlag: 'arrears',
  },
  {
    id: 'rcti-cashflow',
    name: 'RCTI Cashflow',
    path: '/reports/rcti-cashflow',
    keywords: ['rcti', 'cashflow', 'cash', 'flow', 'payments', 'income'],
    icon: Wallet,
    section: 'reports',
    requiredTier: 'pro',
    requiredRoles: ADMIN_REPORT_PAGE_ROLES,
  },
  // ===== REPORTS - Portfolio =====
  {
    id: 'loans',
    name: 'Loan List',
    path: '/reports/loans',
    keywords: ['loans', 'list', 'all', 'portfolio', 'search'],
    icon: FileBarChart,
    section: 'reports',
  },
  {
    id: 'customer-portfolio',
    name: 'Customer Portfolio',
    path: '/reports/customer-portfolio',
    keywords: ['customer', 'portfolio', 'clients', 'borrowers'],
    icon: Users2,
    section: 'reports',
    requiredTier: 'pro',
  },
  {
    id: 'refinance-intelligence',
    name: 'Client Retention',
    path: '/reports/refinance-intelligence',
    keywords: ['refinance', 'intelligence', 'refi', 'conversion'],
    icon: RefreshCw,
    section: 'reports',
    requiredTier: 'enterprise',
  },
  {
    id: 'acquired-books',
    name: 'Acquired Books',
    path: '/reports/acquired-books',
    keywords: ['acquired', 'books', 'purchased', 'bought'],
    icon: Briefcase,
    section: 'reports',
    requiredTier: 'enterprise',
  },

  // ===== ACQUISITION ANALYSIS =====
  {
    id: 'acquisition-analysis',
    name: 'Acquisition Analysis',
    path: '/acquisition-analysis',
    keywords: ['acquisition', 'analysis', 'purchase', 'buy', 'trail'],
    icon: TrendingUp,
    section: 'reports',
    requiredRoles: ['administrator', 'super_admin'],
    requiredTier: 'enterprise',
  },

  // ===== SETTINGS =====
  {
    id: 'settings',
    name: 'My Settings',
    path: '/settings',
    keywords: ['settings', 'preferences', 'profile', 'account', 'my'],
    icon: Settings,
    section: 'settings',
  },
  {
    id: 'audit-log',
    name: 'Audit Log',
    path: '/settings/audit-log',
    keywords: ['audit', 'log', 'history', 'activity', 'changes'],
    icon: Shield,
    section: 'settings',
    requiredRoles: ['office_admin', 'administrator', 'super_admin'],
  },
  {
    id: 'billing',
    name: 'Billing',
    path: '/settings/billing',
    keywords: ['billing', 'subscription', 'payment', 'invoice', 'plan'],
    icon: CreditCard,
    section: 'settings',
    requiredRoles: ['administrator', 'super_admin'],
  },
  {
    id: 'help',
    name: 'Help & Support',
    path: '/help',
    keywords: ['help', 'support', 'faq', 'guide', 'permissions', 'roles', 'plans', 'pricing', 'contact'],
    icon: HelpCircle,
    section: 'settings',
  },
];

// Section display names and order
export const SECTION_CONFIG = {
  core: { name: 'Pages', order: 1 },
  offices: { name: 'Offices', order: 2 },
  reports: { name: 'Reports', order: 3 },
  settings: { name: 'Settings', order: 4 },
} as const;

// Marketing is allowed ONLY Lead Capture + Broker Activity destinations. Every
// other palette entry (dashboard, commissions, upload, offices, reports,
// acquisition, settings, connected-accounts, AI assistant) is denied. This is a
// deny-by-default allow-list so newly added denied destinations never leak into
// marketing's command palette (marketing RBAC §6 palette lockdown).
function isMarketingAllowedPath(path: string): boolean {
  return path.startsWith('/leads') || path.startsWith('/broker-activity');
}

// Helper to filter pages based on user role and subscription
export function getVisiblePages(
  userRole: UserRole | undefined,
  subscriptionTier: SubscriptionTier,
  /** `companyData.featureFlags`. Omitted = nothing suppressed (fail-open). */
  featureFlags?: Record<string, unknown> | null
): SearchablePage[] {
  const tierOrder: SubscriptionTier[] = ['plus', 'pro', 'enterprise'];
  const currentTierIndex = tierOrder.indexOf(subscriptionTier);

  return SEARCHABLE_PAGES.filter((page) => {
    // Skip disabled/parked features
    if (page.disabled) return false;

    // Marketing: deny-by-default, only Lead Capture / Broker Activity pages.
    if (userRole === 'marketing' && !isMarketingAllowedPath(page.path)) {
      return false;
    }

    // Check role requirement
    if (page.requiredRoles && userRole) {
      if (!page.requiredRoles.includes(userRole)) {
        return false;
      }
    }

    // Tenant-suppressed reports: RequireTenantReport bounces the route, so a hit
    // here would navigate the user to an unrelated report. Fail-open, so only an
    // explicit `featureFlags[flag] = false` removes the entry.

    if (page.suppressibleFlag && isReportSuppressedForTenant(featureFlags, page.suppressibleFlag)) {
      return false;
    }

    // Check subscription tier requirement
    if (page.requiredTier) {
      const requiredTierIndex = tierOrder.indexOf(page.requiredTier);
      if (currentTierIndex < requiredTierIndex) {
        return false;
      }
    }

    return true;
  });
}

// Simple fuzzy search that matches keywords and page name
export function searchPages(
  pages: SearchablePage[],
  query: string
): SearchablePage[] {
  if (!query.trim()) return pages;

  const normalizedQuery = query.toLowerCase().trim();
  const queryWords = normalizedQuery.split(/\s+/);

  return pages
    .map((page) => {
      const searchText = [
        page.name.toLowerCase(),
        ...page.keywords,
      ].join(' ');

      // Calculate match score
      let score = 0;
      for (const word of queryWords) {
        if (page.name.toLowerCase().includes(word)) {
          score += 10; // Higher weight for name matches
        }
        if (page.keywords.some((k) => k.includes(word))) {
          score += 5;
        }
        if (searchText.includes(word)) {
          score += 1;
        }
      }

      return { page, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ page }) => page);
}
